import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Stack,
  Switch,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import VisibilityIcon from "@mui/icons-material/Visibility";
import { CircularLoading, NoDataWarning } from "@/components/common";
import { OrganizationAffiliation } from "fhir/r5";

interface AcceptedPlansTabProps {
  loading: boolean;
  organizationAffiliation: OrganizationAffiliation[];
  filters: {
    organization: string;
    plan: string;
    procedure: string;
  };
  onEdit: (affiliation: OrganizationAffiliation) => void;
  onToggleActive: (affiliation: OrganizationAffiliation) => void;
  onViewDetails: (affiliation: OrganizationAffiliation) => void;
}

// Função auxiliar para agrupar afiliações por organização
const groupAffiliationsByOrganization = (
  affiliations: OrganizationAffiliation[],
  filters: { organization: string; plan: string; procedure: string }
) => {
  const normalizedFilters = {
    organization: filters.organization.toLowerCase().trim(),
    plan: filters.plan.toLowerCase().trim(),
    procedure: filters.procedure.toLowerCase().trim(),
  };

  const filteredAffiliations = affiliations.filter((aff) => {
    const organizationName =
      aff.participatingOrganization?.display?.toLowerCase() || "";
    const planName =
      aff.extension
        ?.find(
          (ext) =>
            ext.url ===
            "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation"
        )
        ?.valueReference?.display?.toLowerCase() || "";
    const procedures = (aff.specialty || [])
      .map((specialty) => specialty.coding?.[0]?.display?.toLowerCase() || "")
      .join(" ");

    return (
      (normalizedFilters.organization === "" ||
        organizationName.includes(normalizedFilters.organization)) &&
      (normalizedFilters.plan === "" ||
        planName.includes(normalizedFilters.plan)) &&
      (normalizedFilters.procedure === "" ||
        procedures.includes(normalizedFilters.procedure))
    );
  });

  return filteredAffiliations.reduce((groups, aff) => {
    const orgId = aff.participatingOrganization?.reference;
    if (!groups[orgId || ""]) {
      groups[orgId || ""] = {
        organizationName: aff.participatingOrganization?.display || "Sem nome",
        affiliations: [],
      };
    }
    groups[orgId || ""].affiliations.push(aff);
    return groups;
  }, {} as Record<string, { organizationName: string; affiliations: OrganizationAffiliation[] }>);
};

export function AcceptedPlansTab({
  loading,
  organizationAffiliation,
  filters,
  onEdit,
  onToggleActive,
  onViewDetails,
}: AcceptedPlansTabProps) {
  if (loading) {
    return <CircularLoading />;
  }

  if (!organizationAffiliation.length) {
    return <NoDataWarning />;
  }

  return (
    <Stack spacing={2}>
      {Object.entries(
        groupAffiliationsByOrganization(organizationAffiliation, filters)
      ).map(([orgId, group]) => (
        <Paper key={orgId} sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight="600" gutterBottom>
            {group.organizationName}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Plano</TableCell>
                  <TableCell>Procedimentos</TableCell>
                  <TableCell align="center">Ações</TableCell>
                  <TableCell align="center">Ativo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.affiliations.map((aff) => (
                  <TableRow hover key={aff.id}>
                    <TableCell>
                      {
                        aff.extension?.find(
                          (ext) =>
                            ext.url ===
                            "https://fluxmed.com.br/fhir/StructureDefinition/insurance-plan-affiliation"
                        )?.valueReference?.display
                      }
                    </TableCell>
                    <TableCell>
                      {aff.specialty
                        ?.map((specialty) => specialty.coding?.[0]?.display)
                        .join(", ")}
                    </TableCell>

                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => onViewDetails(aff)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => onEdit(aff)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>

                    <TableCell align="center">
                      <Switch
                        checked={
                          aff.active === undefined || aff.active === true
                        }
                        onChange={() => onToggleActive(aff)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}
    </Stack>
  );
}
